import {AfterViewInit, Component, Input, OnDestroy} from '@angular/core';
import { Chart } from 'angular-highcharts';
import {TransactionService} from '../../../services/transaction.service';
import * as _ from 'lodash';
import {Router} from '@angular/router';
import {HighchartsService} from '../../../services/highcharts.service';
import * as moment from 'moment-timezone';
import {DatesService} from '../../../services/dates.service';
import {Title} from '@angular/platform-browser';
import {Subscription} from 'rxjs/Subscription';

@Component({
    selector: 'app-transactions-in-out-bound',
    templateUrl: './transactions-in-out-bound.component.html',
    styleUrls: ['./transactions-in-out-bound.component.scss'],
    providers: [ HighchartsService, DatesService ]
})
export class TransactionsInOutBoundComponent implements AfterViewInit, OnDestroy {

    @Input() chartOnly  = false;
    @Input() semiOnly   = false;
    @Input() height     = '';
    @Input() title      = '';
    @Input() preloadData: any = null;

    private preloadSubscription: Subscription;
    private seriesSubscription: Subscription;
    private detailsSubscription: Subscription;

    companies: Array<any> = [];
    ediTypes:  Array<object>  = [];

    drilldownLevel = 0;

    minDate:   Date = new Date(2000, 1, 1);
    startYear: Date = new Date(2010, 1, 1);

    drilldowns: any = {};

    filters: any = {
        aggrigatedBy: HighchartsService.DRILLDOWN_YEAR,
        CO1_ID: [],
        ED1_TYPE: [],
        ED1_MODIFIED_ON_FROM: null,
        ED1_MODIFIED_ON_TO: null
    };

    aggrigatedBy: { [key: number]: number; } = {};

    chart: Chart;

    constructor(
        private transactionService: TransactionService,
        private highchartsService: HighchartsService,
        private datesService: DatesService,
        private router: Router,
        private appTitle: Title
    ) {
        this.aggrigatedBy[1] = HighchartsService.DRILLDOWN_YEAR;
        this.aggrigatedBy[2] = HighchartsService.DRILLDOWN_MONTH;
        this.aggrigatedBy[3] = HighchartsService.DRILLDOWN_DAY;
    }

    ngAfterViewInit() {
        if (!this.chartOnly) {
            this.appTitle.setTitle('EDI - Transactions');
        }
        if (this.preloadData === null) {
            this.preloadSubscription = this.transactionService.preloadData().subscribe(res => {
                this.handlePreloadData( res );
            }, () => {
                // failed to load data
            });
        } else {
            this.handlePreloadData( this.preloadData );
        }
    }

    ngOnDestroy() {
        if (this.preloadSubscription) {
            this.preloadSubscription.unsubscribe();
        }
        if (this.seriesSubscription) {
            this.seriesSubscription.unsubscribe();
        }
        if (this.detailsSubscription) {
            this.detailsSubscription.unsubscribe();
        }
    }

    handlePreloadData( res ) {
        this.companies = res.companies;
        this.ediTypes  = res.ediTypes;

        const me = this;
        this.chart = new Chart({
            chart: {
                type: 'pie',
                plotBackgroundColor: null,
                plotBorderWidth: 0,
                plotShadow: false,
                events: {
                    drillup: function (e) {
                        me.drilldownLevel--;
                    },
                    drilldown: function (e) {
                        if (!e.seriesOptions) {

                            const chart = this;
                            const serie = me.highchartsService.getSerieByPoint(me.chart, e.point);

                            if (serie) {
                                if (me.drilldownLevel < 1) {
                                    chart.addSeriesAsDrilldown(e.point, {
                                        type: serie.drilldownType,
                                        name: serie.name,
                                        data: serie.drilldown,
                                        innerSize: '40%'
                                    });
                                    me.drilldownLevel++;
                                } else {

                                    const params = {
                                        CO1_ID:     me.filters['CO1_ID'],
                                        ED1_TYPE:   serie.ED1_TYPE,
                                        ED1_IN_OUT: serie.ED1_IN_OUT,
                                        aggrigatedBy:  me.aggrigatedBy[me.drilldownLevel]
                                    };

                                    if ( me.drilldownLevel > 1 ) {
                                        const startDate  = moment.tz(new Date(e.point.x), 'UTC');
                                        let endDate = new Date( Date.UTC(startDate.year(), 11, 31) );
                                        if ( me.aggrigatedBy[me.drilldownLevel] == HighchartsService.DRILLDOWN_MONTH ) {
                                            endDate = new Date( Date.UTC(startDate.year(), 11, 31) );
                                        } else if ( me.aggrigatedBy[me.drilldownLevel] == HighchartsService.DRILLDOWN_DAY ) {
                                            endDate = new Date( Date.UTC(startDate.year(), startDate.month(), startDate.daysInMonth()) );
                                        }

                                        params['ED1_MODIFIED_ON_FROM'] = me.datesService.format(
                                            startDate.toString(), '00:00:00', 'UTC'
                                        );
                                        params['ED1_MODIFIED_ON_TO']   = me.datesService.format(
                                            moment(endDate).tz('UTC').toString(), '23:59:59', 'UTC'
                                        );
                                    } else {
                                        if (me.filters['ED1_MODIFIED_ON_FROM']) {
                                            params['ED1_MODIFIED_ON_FROM'] = me.datesService.format(me.filters['ED1_MODIFIED_ON_FROM']);
                                        }
                                        if (me.filters['ED1_MODIFIED_ON_TO']) {
                                            params['ED1_MODIFIED_ON_TO'] = me.datesService.format(
                                                me.filters['ED1_MODIFIED_ON_TO'], '23:59:59'
                                            );
                                        }
                                    }

                                    // Show the loading label
                                    chart.showLoading('Loading ...');
                                    me.seriesSubscription = me.transactionService.getSeries([params]).subscribe( data => {
                                        chart.hideLoading();

                                        data.serie.forEach( serieData => {
                                            const drilldownData = serieData.data[0].data.map( item => {
                                                const result = {};
                                                result['name']       = serie.name;
                                                result['x']          = item[0];
                                                result['y']          = item[1];
                                                result['ED1_TYPE']   = serie.ED1_TYPE;
                                                result['ED1_IN_OUT'] = serie.ED1_IN_OUT;

                                                if ( me.aggrigatedBy[me.drilldownLevel] > HighchartsService.DRILLDOWN_DAY) {
                                                    result['drilldown'] = [];
                                                    result['drilldownType']  = 'column';
                                                }
                                                return result;
                                            });

                                            chart.addSeriesAsDrilldown(e.point, {
                                                type: serie.drilldownType,
                                                name: serie.name,
                                                data: drilldownData,
                                                tooltip: {
                                                    headerFormat: '<span style=\'font-size: 10px\'>{point.x:%Y, %b %d}</span><br/>',
                                                    pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'
                                                },
                                                dataLabels: {
                                                    format: '{point.y:,.0f}'
                                                }
                                            });

                                            me.drilldownLevel ++;
                                        });
                                    });
                                }
                            }
                        }
                    }
                }
            },
            title: {
                text: this.title,
                y: 20
            },
            tooltip: {
                pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>'
            },
            plotOptions: {
                pie: {
                    startAngle: this.semiOnly ? -90 : null,
                    endAngle:   this.semiOnly ?  90 : null,
                    center: ['50%', this.semiOnly ? '80%' : '50%'],
                    showInLegend: false
                },
                series: {
                    dataLabels: {
                        enabled: true,
                        padding: 1,
                        format: '{point.name}: {point.y:,.0f}'
                    }
                }
            },
            xAxis: {
                title: {
                    text: 'Timeline'
                },
                type: 'datetime',
            },

            yAxis: {
                min: 0,
                title: {
                    text: 'Number of transactions'
                }
            },
        });
        this.onSubmit();
    }

    loadChartData(params: any = null) {
        this.detailsSubscription = this.transactionService.getInOutBoundReport(params).subscribe(result => {

            this.drilldownLevel = 0;

            this.highchartsService.destroyDrillDown( this.chart );
            this.highchartsService.removeAllSeries( this.chart );

            const data = [];
            result.data.forEach(function (item) {

                const drilldowns = [];
                item.drilldown.forEach(drilldown => {
                    drilldowns.push({
                        'name': drilldown.name ? drilldown.name : 'Unknown',
                        'y': parseInt(drilldown.total, 10),
                        drilldownType: 'column',
                        drilldown: [],
                        ED1_TYPE: drilldown.name,
                        ED1_IN_OUT: drilldown.ED1_IN_OUT
                    });
                });

                data.push({
                    name: item.name ? item.name : 'No Data',
                    y: parseInt(item.totals, 10),
                    drilldownType: 'pie',
                    drilldown: drilldowns
                });
            });


            this.chart.addSerie({
                innerSize: '50%',
                data: data,
                dataLabels: {
                    enabled: true,
                    padding: 5
                }
            });
        });
    }

    onSubmit() {

        const filters = _.clone(this.filters);

        filters['CO1_ID[]']   = filters['CO1_ID'];
        delete filters['CO1_ID'];

        filters['ED1_TYPE[]'] = filters['ED1_TYPE'];
        delete filters['ED1_TYPE'];

        if (filters['ED1_MODIFIED_ON_FROM']) {
            filters['ED1_MODIFIED_ON_FROM'] = this.datesService.format(filters['ED1_MODIFIED_ON_FROM']);
        }
        if (filters['ED1_MODIFIED_ON_TO']) {
            filters['ED1_MODIFIED_ON_TO'] = this.datesService.format(filters['ED1_MODIFIED_ON_TO'], '23:59:59');
        }

        this.loadChartData(filters);
    }

    onCancel(): void {
        this.router.navigate(['/transactions']);
    }
}



// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/views/transactions/transactions-in-out-bound/transactions-in-out-bound.component.ts